/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { ClipboardCheckIcon, ClipboardIcon, EyeIcon, EyeOffIcon } from "lucide-react"
import { useEffect, useState } from "react"

import { cn } from "@/utils/cn"

import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { valueOrDash } from "../../utils/value-or-dash"
import { useViewModel } from "../app/app-hooks"
import { Button } from "../primitives/button"
import { IconSwap } from "./icon-swap"
import { Truncate } from "./truncate"

interface Props {
    /**
     * Value to display and copy.
     * Nullish or empty values show an em dash and disable the buttons.
     */
    value: string | null | undefined
    /**
     * Masks the value with asterisks and adds a button to reveal it.
     *
     * @default false
     */
    masked?: boolean
}

/**
 * Displays a value with copy support, truncating it in the middle when needed.
 * Masked values can also be revealed.
 */
export const ClipboardValue = ({ value, masked = false }: Props) => {
    const [hidden, setHidden] = useState(masked)
    const [copied, setCopied] = useState(false)

    const loggerVM = useViewModel(LoggerViewModel)

    const copyable = value ?? ""
    const hasValue = copyable.length > 0

    // Match the value's length so revealing it does not change the layout.
    const text = hasValue && hidden ? "*".repeat(copyable.length) : valueOrDash(value)

    const copyToClipboard = () => {
        // Clipboard API is unavailable outside a secure context, such as when using
        // the app over a LAN IP. Check before calling `writeText`, which would throw.
        if (!navigator.clipboard) {
            loggerVM.logErrorMessage("Clipboard unavailable: the page is not in a secure context")
            return
        }

        navigator.clipboard
            .writeText(copyable)
            .then(() => setCopied(true))
            .catch((err) => {
                loggerVM.logErrorMessage(err)
            })
    }

    // Show the copied state briefly, then return to the copy icon.
    useEffect(() => {
        if (!copied) return

        const timer = setTimeout(() => setCopied(false), 1_000)
        return () => clearTimeout(timer)
    }, [copied])

    return (
        <span className="inline-flex items-center gap-1">
            <Truncate kind="middle" start="5ch" end="5ch" showTitle={!masked} text={text} />

            {/*
             * Keep the buttons in the layout even when there is no value so every
             * row has the same height. `invisible` also removes them from tab order
             * and the accessibility tree.
             */}
            <span className="inline-flex">
                {masked && (
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        className={cn({ invisible: !hasValue })}
                        aria-label="Show value"
                        aria-pressed={!hidden}
                        onClick={() => setHidden(!hidden)}
                    >
                        <IconSwap icons={[EyeIcon, EyeOffIcon]} active={hidden ? 0 : 1} iconClassName="size-4" />
                    </Button>
                )}

                <Button
                    variant="ghost"
                    size="icon-xs"
                    className={cn({ invisible: !hasValue })}
                    aria-label="Copy to clipboard"
                    title="Copy to clipboard"
                    onClick={copyToClipboard}
                >
                    <IconSwap
                        icons={[ClipboardIcon, ClipboardCheckIcon]}
                        active={copied ? 1 : 0}
                        iconClassName="size-4"
                    />
                </Button>
            </span>
        </span>
    )
}
